import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { coursesApi } from '../services/api';
import { BookOpen, Clock } from 'lucide-react';

interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail?: string;
  progress?: number;
  category: {
    name: string;
    color?: string;
  };
  duration: number;
  lessons: any[];
  isEnrolled?: boolean;
}

const MyCourses: React.FC = () => {
  const { } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyCourses();
  }, []);

  const loadMyCourses = async () => {
    try {
      setLoading(true);
      const response = await coursesApi.getEnrolled();
      const enrolledCourses = response.data.data?.courses || [];
      setCourses(enrolledCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">Mis Cursos</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm lg:text-base">Gestiona y continúa tu aprendizaje</p>
        </div>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-12 cyber-container">
          <BookOpen size={64} className="mx-auto text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-400 mb-2">
            No tienes cursos inscritos
          </h3>
          <p className="text-gray-600 dark:text-gray-500 mb-6">
            Explora nuestro catálogo y comienza tu journey de aprendizaje
          </p>
          <Link
            to="/dashboard"
            className="cyber-button-primary inline-flex items-center gap-2"
          >
            <BookOpen size={16} />
            Explorar Cursos
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {courses.map((course) => (
            <div key={course.id} className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900/90 dark:to-gray-800/90 rounded-lg border border-gray-300 dark:border-gray-700/50 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20 group p-5">
              {/* Content */}
              <div className="space-y-4">
                {/* Header with icon */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-200 to-blue-200 dark:from-cyan-500/20 dark:to-blue-500/20 rounded-lg flex items-center justify-center border border-cyan-300 dark:border-cyan-500/30">
                    <BookOpen size={20} className="text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span 
                        className="px-2 py-1 rounded-md text-xs font-semibold"
                        style={{ 
                          backgroundColor: (course.category.color || '#3b82f6') + '20', 
                          color: course.category.color || '#3b82f6'
                        }}
                      >
                        {course.category?.name || 'Sin categoría'}
                      </span>
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
                        <Clock size={12} />
                        <span>{Math.round(course.duration / 60)}h</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-200 leading-tight truncate">
                      {course.title}
                    </h3>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed line-clamp-2">
                  {course.description}
                </p>

                {/* Progress Section */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Progreso</span>
                    <span className="text-cyan-500 dark:text-cyan-400 font-bold">{course.progress || 0}%</span>
                  </div>
                  
                  <div className="w-full bg-gray-300 dark:bg-gray-700/50 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-cyan-400 to-blue-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${course.progress || 0}%` }}
                    />
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{course.lessons?.length || 0} lecciones</span>
                    <span>{course.progress === 100 ? '✅ Completado' : 'En progreso'}</span>
                  </div>
                </div>

                {/* Action Button */}
                <Link
                  to={`/course/${course.id}`}
                  className="block w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 text-center text-sm"
                >
                  {course.progress === 100 ? '🏆 Revisar curso' : '▶️ Continuar'}
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCourses;